import { useDispatch } from 'react-redux';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle } from 'react';
import { ProgressIndicator, Stack, StackItem } from '@optimus-web/core';
import { LocalizedPropertyObject, lt, useFeatureFlag, useScope } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { Service } from '@mews-commander/data-access';
import { useCommanderSelector } from '@mews-commander/utils';
import { BILLING_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { getBills, getUniqueBillOwnerIdsByBillIds } from '@mews-commander/legacy-entities/src/ducks/bills';
import { refetchBills } from '../../../../../billing/ducks/bills';
import { useServicesGetAll } from '../../../../queries/useServicesGetAll';
import { useProductsGetAll } from '../../../../queries/useProductsGetAll';
import { SubmitHandlers } from '../../../../queries/useProductServiceOrdersAdd';
import { useAddProductForm } from '../../hooks/useAddProductForm';
import { SettingsTab } from '../SettingsTab';
import { ProductsTab } from '../ProductsTab';
import { AddProductFormRef } from '../AddProductDialog';
import { TotalBarWithData } from '../TotalBarWithData';
import { AddProductStep } from './constants';

interface AddProductDialogContentProps {
    currentFormRef: ForwardedRef<AddProductFormRef>;
    accountId: string;
    serviceId: string;
    billId?: string;
    scopedReservationIds?: string[];
    onClose: () => void;
    setLoading: (loading: boolean) => void;
    setDialogName: (names: LocalizedPropertyObject) => void;
    activeStep?: AddProductStep;
    setActiveStep?: (step: AddProductStep) => void;
}

export const TwoStepAddProductDialogContent = ({
    currentFormRef,
    accountId,
    serviceId,
    billId,
    setLoading,
    onClose,
    setDialogName,
    activeStep,
    setActiveStep,
    scopedReservationIds,
}: AddProductDialogContentProps) => {
    const dispatch = useDispatch();

    const scope = useScope() || '';

    const bills = useCommanderSelector(getBills);

    const uniqueBillOwners = useCommanderSelector((state) =>
        getUniqueBillOwnerIdsByBillIds(state, { ids: Object.values(bills).map((bill) => bill.id) })
    );

    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);

    const {
        data: { service, businessSegments },
    } = useServicesGetAll(serviceId, { businessSegments: true });
    const { data: productsData } = useProductsGetAll([serviceId]);

    useEffect(() => {
        if (service) {
            setDialogName(service.names);
        }
    }, [service, setDialogName]);

    if (!service || !productsData) {
        return null;
    }

    const formProps = {
        ref: currentFormRef,
        accountId,
        scopedReservationIds,
        billId,
        service,
        onSubmitting: () => setLoading(true),
        onSubmitted: () => setLoading(false),
        onSuccess: () => {
            const billIds = billId ? [billId] : [];
            const accountIds = !billId && newRoutingRulesPOSEnabled ? uniqueBillOwners : [accountId];

            dispatch(
                refetchBills({
                    billIds,
                    scope,
                    accountIds,
                    reservationGroupId: scope,
                    extent: BILLING_EXTENT,
                })
            );

            onClose();
        },
    };

    return (
        <Stack spacing="300" vertical>
            <Stack vertical spacing="400" fill>
                <ProgressIndicator
                    steps={[
                        {
                            value: AddProductStep.Product,
                            label: lt('AddServiceToReservationTabsProducts'),
                            completed: activeStep !== AddProductStep.Product,
                            editable: true,
                        },
                        {
                            value: AddProductStep.Settings,
                            label: lt('AddServiceToReservationTabsAdditionalSettings'),
                            completed: false,
                            editable: false,
                        },
                    ]}
                    activeStep={activeStep}
                    onStepChange={setActiveStep}
                />
                <Form {...formProps}>
                    <Stack vertical spacing="300">
                        <StackItem>
                            {activeStep === AddProductStep.Product ? (
                                <ProductsTab
                                    accountId={accountId}
                                    isVirtualBill={!billId}
                                    service={service}
                                    productsData={productsData}
                                    scopedReservationIds={scopedReservationIds}
                                />
                            ) : null}
                            {activeStep === AddProductStep.Settings ? (
                                <SettingsTab service={service} businessSegments={businessSegments} />
                            ) : null}
                        </StackItem>
                        <StackItem>
                            <TotalBarWithData service={service} productsData={productsData} />
                        </StackItem>
                    </Stack>
                </Form>
            </Stack>
        </Stack>
    );
};

interface FormProps
    extends Pick<AddProductDialogContentProps, 'billId' | 'accountId' | 'scopedReservationIds'>,
        SubmitHandlers {
    service: Service;
}

const Form = forwardRef(({ children, ...props }: React.PropsWithChildren<FormProps>, ref) => {
    const { renderFormContent, submit, hasDirtyFields, validate } = useAddProductForm(props);

    useImperativeHandle(
        ref,
        () => ({
            submit,
            hasDirtyFields,
            validate,
        }),
        [submit, hasDirtyFields, validate]
    );

    return renderFormContent(<>{children}</>);
});
